import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { AccountSettingsService } from './account-setting.service';
import { IAccountSetting } from './account-setting-interfaces';
import { AccountDetailsComponent } from './account-details/account-details.component';
import { MFValidators } from 'src/app/shared/validators/mf-validators';
import { getFullName } from 'src/app/shared/utils/full-name.util';
import { HelperService } from 'src/app/shared/utils/helper.service';
import { IUser } from 'src/app/user-management/user-management-interfaces';
import { StateManagementService } from 'src/app/shared/services/state-management.service';

@Component({
  selector: 'app-account-settings',
  templateUrl: './account-settings.component.html',
  styleUrls: ['./account-settings.component.scss'],
})
export class AccountSettingsComponent implements OnInit {
  @ViewChild(AccountDetailsComponent) accountDetailRef:
    | AccountDetailsComponent
    | undefined;

  formGroup!: FormGroup;
  readonly LANG_CONST = LANG_CONST;
  stepper = 1;
  SuccessMessage = '';
  phoneNumber!: string;
  firstName!: string;
  lastName!: string;
  email!: string;
  code: string = '';
  phone: string = '';
  user!: IUser;
  profileImageSrc: any = 'assets/images/portrait-happy.jpg';
  siteList: string[] = [];

  /**
   * @description constructor injects all necesary services
   * @param { AddUserComponent } dialogRef
   */
  constructor(
    public dialogRef: MatDialogRef<AccountSettingsComponent>,
    private fb: FormBuilder,
    private accountSettingService: AccountSettingsService,
    private helperService: HelperService,
    private stateManagementService: StateManagementService
  ) {}

  ngOnInit(): void {
    this.createControls();

    this.user = this.stateManagementService.getUser();
    this.setFormValues(this.user);
    this.profileImageSrc = this.user?.userImgUrl || this.profileImageSrc;

    if (this.user.name === '') {
      this.accountSettingService.getMyInfo().subscribe({
        next: (accountSetting: IAccountSetting) => {
          this.setFormValues(accountSetting);
          this.profileImageSrc = accountSetting.userImgUrl;
        },
      });
    }
  }
  setFormValues(accountSetting: IAccountSetting | IUser) {
    this.formGroup.patchValue({
      accountDetails: {
        name: getFullName(
          accountSetting?.firstName,
          accountSetting?.middleName,
          accountSetting?.lastName
        ),
        phone: accountSetting?.phoneNumber,
        email: accountSetting?.email,
        password: '', //Password is a dummy field.
        faceId:
          accountSetting?.faceId?.toString().toLowerCase() === 'false'
            ? false
            : true,
        twoStepVerification: accountSetting?.mFAEnabled,
        pushNotificationEnabled:
          accountSetting?.pushNotificationEnabled?.toString().toLowerCase() ===
          'false'
            ? false
            : true,
      },
    });
    this.formGroup.patchValue({
      accountChangeName: {
        firstName: accountSetting?.firstName,
        middleName: accountSetting?.middleName ?? '',
        lastName: accountSetting?.lastName,
      },
    });
    this.setPhoneNumber(
      accountSetting?.phoneNumber ? accountSetting?.phoneNumber : ''
    );
    this.phone = accountSetting?.phoneNumber
      ? accountSetting?.phoneNumber.replace(this.code, '')
      : '';
    this.formGroup.patchValue({
      accountChangePhone: {
        region: this?.code,
        phone: this?.phone,
      },
    });

    this.accountDetailRef?.setPhoneNumber();
    this.email = accountSetting?.email ? accountSetting?.email : '';
  }

  /**
   * @description Create all child component into parents
   * @returns void
   */
  createControls(): void {
    this.formGroup = this.fb.group({
      accountDetails: this.fb.group({
        name: [''],
        phone: [''],
        email: [''],
        password: [''],
        faceId: [],
        twoStepVerification: [],
        pushNotificationEnabled: [],
      }),
      accountChangeName: this.fb.group({
        firstName: [
          '',
          MFValidators.compose([
            MFValidators.required,
            MFValidators.whitespace,
            MFValidators.maxLength(32),
            MFValidators.nameWithAllowedSpecialChars,
          ]),
        ],
        middleName: [
          '',
          (control: AbstractControl) =>
            control.value?.trim()
              ? MFValidators.compose([
                  MFValidators.whitespace,
                  MFValidators.maxLength(32),
                  MFValidators.nameWithAllowedSpecialChars,
                ])?.(control) ?? null
              : null,
        ],
        lastName: [
          '',
          MFValidators.compose([
            MFValidators.required,
            MFValidators.whitespace,
            MFValidators.maxLength(32),
            MFValidators.nameWithAllowedSpecialChars,
          ]),
        ],
      }),
      accountChangePhone: this.fb.group({
        region: [
          '',
          MFValidators.compose([MFValidators.required, MFValidators.region]),
        ],
        phone: [
          '',
          MFValidators.compose([MFValidators.required, MFValidators.usCaPhone]),
        ],
      }),
    });
  }

  /**
   * @description close modal dialogue with help of material service
   * @returns void
   */
  closeDialog(): void {
    this.dialogRef.close();
  }

  /**
   * @description setPhoneNumber function validates phone number and apply masking on the number.
   */
  setPhoneNumber(phone: string): void {
    this.helperService.validatePhoneFormat(phone).subscribe(
      ({
        phoneNumber,
        region,
        code,
      }: {
        phoneNumber: string;
        region: string;
        code: string;
      }) => {
        this.code = code;
      },
      (err: string) => {
        if (window.__dev__) console.log('validatePhoneFormat', err);
        this.code = '';
      }
    );
  }
}
